/**
 * Lootboxes / Dungeon Chests
 *
 * Chests dropped by bosses and found in treasure rooms.
 * Each chest contains guaranteed loot based on its tier.
 *
 * Structure:
 * - tier: Matches dungeon tier (1-5)
 * - rarity: Visual rarity of the chest itself
 * - dropTable: Defines what can drop and at what rates
 * - guaranteedDrops: Minimum number of items
 * - bonusDropChance: Chance for extra drops
 *
 * Opening a chest:
 * 1. Roll for each drop slot (guaranteed + bonus)
 * 2. For each slot, roll category (equipment/material/consumable)
 * 3. For equipment, roll rarity from weights
 * 4. Generate item at chest's dungeonLevel
 */

import { ITEM_CATEGORIES } from './consumables';

/**
 * Lootbox definitions by tier
 * Each dungeon tier drops its corresponding chest
 */
export const LOOTBOXES = {
  // ===========================================
  // TIER 1 - Ancient Ruins Chest
  // ===========================================
  chest_tier1: {
    id: 'chest_tier1',
    name: 'Ancient Chest',
    icon: '📦',
    category: ITEM_CATEGORIES.LOOTBOX,
    rarity: 'common',
    tier: 1,
    description: 'A dusty chest from the Ancient Ruins. Contains basic equipment and materials.',
    stackSize: 99,
    usable: true,
    sellValue: 50,
    dropTable: {
      // Category weights (must sum to 100)
      equipment: 40,      // 40% chance per slot
      material: 35,       // 35% chance per slot
      consumable: 25,     // 25% chance per slot
      // Equipment rarity weights within equipment drops
      equipmentRarity: {
        common: 60,
        uncommon: 30,
        rare: 10,
      },
      // Possible materials
      materials: ['bone', 'leather', 'cloth', 'rustyScrap'],
      // Possible consumables
      consumables: ['minorHealthPotion', 'healthPotion'],
    },
    guaranteedDrops: 2,   // Always get at least 2 items
    bonusDropChance: 0.3, // 30% chance for each bonus drop (up to 2)
    maxBonusDrops: 2,
    dungeonLevel: 10,     // Equipment drops at this level
  },

  // ===========================================
  // TIER 2 - Catacomb Chest
  // ===========================================
  chest_tier2: {
    id: 'chest_tier2',
    name: 'Catacomb Chest',
    icon: '🗃️',
    category: ITEM_CATEGORIES.LOOTBOX,
    rarity: 'uncommon',
    tier: 2,
    description: 'An iron-bound chest from the Dark Catacombs. Contains improved gear.',
    stackSize: 99,
    usable: true,
    sellValue: 150,
    dropTable: {
      equipment: 45,
      material: 30,
      consumable: 25,
      equipmentRarity: {
        common: 30,
        uncommon: 50,
        rare: 18,
        epic: 2,
      },
      materials: ['bone', 'leather', 'cloth', 'ironOre', 'darkSteel', 'essence'],
      consumables: ['healthPotion', 'greaterHealthPotion', 'potionOfStrength', 'potionOfDefense'],
    },
    guaranteedDrops: 2,
    bonusDropChance: 0.35,
    maxBonusDrops: 2,
    dungeonLevel: 25,
  },

  // ===========================================
  // TIER 3 - Temple Chest
  // ===========================================
  chest_tier3: {
    id: 'chest_tier3',
    name: 'Temple Chest',
    icon: '📿',
    category: ITEM_CATEGORIES.LOOTBOX,
    rarity: 'rare',
    tier: 3,
    description: 'A gilded chest from the Corrupted Temple. Contains powerful artifacts.',
    stackSize: 99,
    usable: true,
    sellValue: 400,
    dropTable: {
      equipment: 50,
      material: 25,
      consumable: 25,
      equipmentRarity: {
        common: 10,
        uncommon: 35,
        rare: 40,
        epic: 14,
        legendary: 1,
      },
      materials: ['darkSteel', 'essence', 'crystal', 'dragonScale', 'sulfur', 'ectoplasm'],
      consumables: ['greaterHealthPotion', 'superiorHealthPotion', 'elixirOfPower', 'potionOfSwiftness'],
    },
    guaranteedDrops: 3,
    bonusDropChance: 0.4,
    maxBonusDrops: 2,
    dungeonLevel: 45,
  },

  // ===========================================
  // TIER 4 - Abyssal Chest
  // ===========================================
  chest_tier4: {
    id: 'chest_tier4',
    name: 'Abyssal Chest',
    icon: '🎁',
    category: ITEM_CATEGORIES.LOOTBOX,
    rarity: 'epic',
    tier: 4,
    description: 'A chest pulsing with void energy from the Abyssal Depths. Contains epic treasures.',
    stackSize: 99,
    usable: true,
    sellValue: 1000,
    dropTable: {
      equipment: 55,
      material: 25,
      consumable: 20,
      equipmentRarity: {
        uncommon: 15,
        rare: 35,
        epic: 40,
        legendary: 9,
        mythic: 1,
      },
      materials: ['voidFragment', 'soulShard', 'dragonScale', 'dragonHeart', 'cosmicFragment'],
      consumables: ['superiorHealthPotion', 'elixirOfPower', 'scrollOfFortitude', 'scrollOfPrecision'],
    },
    guaranteedDrops: 3,
    bonusDropChance: 0.45,
    maxBonusDrops: 3,
    dungeonLevel: 65,
  },

  // ===========================================
  // TIER 5 - Void Chest
  // ===========================================
  chest_tier5: {
    id: 'chest_tier5',
    name: 'Void Chest',
    icon: '🌌',
    category: ITEM_CATEGORIES.LOOTBOX,
    rarity: 'legendary',
    tier: 5,
    description: 'A chest forged from pure void essence. Contains legendary and mythic treasures.',
    stackSize: 99,
    usable: true,
    sellValue: 3000,
    dropTable: {
      equipment: 60,
      material: 20,
      consumable: 20,
      equipmentRarity: {
        rare: 15,
        epic: 35,
        legendary: 40,
        mythic: 10,
      },
      materials: ['voidEssence', 'cosmicFragment', 'divineEssence', 'starMetal', 'eterniumIngot'],
      consumables: ['superiorHealthPotion', 'elixirOfPower', 'blessingToken', 'luckyCharm'],
    },
    guaranteedDrops: 4,
    bonusDropChance: 0.5,
    maxBonusDrops: 3,
    dungeonLevel: 85,
  },

  // ===========================================
  // SPECIAL CHESTS
  // ===========================================

  // Boss-specific chest (guaranteed from any boss)
  chest_boss: {
    id: 'chest_boss',
    name: 'Boss Treasure',
    icon: '👑',
    category: ITEM_CATEGORIES.LOOTBOX,
    rarity: 'epic',
    tier: 0, // Tier set dynamically based on dungeon
    description: 'A magnificent chest dropped by a dungeon boss. Guaranteed rare+ equipment.',
    stackSize: 99,
    usable: true,
    sellValue: 500,
    dropTable: {
      equipment: 70,      // High equipment chance
      material: 20,
      consumable: 10,
      equipmentRarity: {
        rare: 40,
        epic: 40,
        legendary: 18,
        mythic: 2,
      },
      materials: ['soulShard', 'essence', 'crystal', 'dragonScale'],
      consumables: ['greaterHealthPotion', 'superiorHealthPotion', 'elixirOfPower'],
    },
    guaranteedDrops: 3,
    bonusDropChance: 0.5,
    maxBonusDrops: 2,
    dungeonLevel: 0, // Set dynamically
  },

  // Rare golden chest (low chance from treasure rooms)
  chest_golden: {
    id: 'chest_golden',
    name: 'Golden Chest',
    icon: '✨',
    category: ITEM_CATEGORIES.LOOTBOX,
    rarity: 'legendary',
    tier: 0,
    description: 'An extremely rare golden chest. Contains premium loot with high legendary chance.',
    stackSize: 99,
    usable: true,
    sellValue: 2000,
    dropTable: {
      equipment: 80,
      material: 10,
      consumable: 10,
      equipmentRarity: {
        epic: 30,
        legendary: 55,
        mythic: 15,
      },
      materials: ['divineEssence', 'starMetal', 'eterniumIngot', 'cosmicFragment'],
      consumables: ['superiorHealthPotion', 'elixirOfPower', 'blessingToken'],
    },
    guaranteedDrops: 4,
    bonusDropChance: 0.6,
    maxBonusDrops: 3,
    dungeonLevel: 0, // Set dynamically
  },
};

/**
 * Get chest for a specific dungeon tier
 */
export function getChestForTier(tier) {
  const chestId = `chest_tier${tier}`;
  return LOOTBOXES[chestId] || LOOTBOXES.chest_tier1;
}

/**
 * Get all lootbox IDs
 */
export function getAllLootboxIds() {
  return Object.keys(LOOTBOXES);
}

/**
 * Check if an item is a lootbox
 */
export function isLootbox(itemId) {
  return itemId in LOOTBOXES || itemId?.startsWith('chest_');
}

/**
 * Get lootbox by ID
 */
export function getLootbox(itemId) {
  return LOOTBOXES[itemId] || null;
}

export default LOOTBOXES;
